import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  Grid,
  Card,
  CardContent,
} from '@mui/material';
import {
  Upload as UploadIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  Rule as RuleIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
} from '@mui/icons-material';
import api from '../../services/api';
import { API_ENDPOINTS } from '../../config/api';

const PolicyRulebook = ({ projectId }) => {
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [rules, setRules] = useState([
    { id: 1, name: 'Protocol Compliance Check', status: 'active', description: 'Validates protocol against regulatory requirements' },
    { id: 2, name: 'Data Completeness Rule', status: 'active', description: 'Ensures all required fields are completed' },
    { id: 3, name: 'Timeline Validation', status: 'inactive', description: 'Checks submission timelines and deadlines' },
  ]);

  useEffect(() => {
    if (projectId) {
      loadUploadedFiles();
    }
  }, [projectId]);

  const loadUploadedFiles = async () => {
    if (!projectId) return;
    setLoading(true);
    setError(null);
    try {
      const response = await api.get(API_ENDPOINTS.policyRulebook.files(projectId));
      if (response.data?.success && Array.isArray(response.data.files)) {
        setUploadedFiles(response.data.files);
      } else {
        setUploadedFiles([]);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load uploaded files');
      setUploadedFiles([]);
      console.error('Error loading files:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedFile(file);
      setUploadDialogOpen(true);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile || !projectId) {
      setError('Please select a file and ensure project is selected');
      return;
    }

    setUploading(true);
    setError(null);
    setSuccess(null);

    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('projectId', projectId);

      const response = await api.post(API_ENDPOINTS.policyRulebook.upload, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      if (!response.data?.success) {
        throw new Error(response.data?.message || 'Upload failed');
      }

      setSuccess('File uploaded successfully');
      setUploadDialogOpen(false);
      setSelectedFile(null);
      loadUploadedFiles();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to upload file');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (fileId) => {
    if (!window.confirm('Are you sure you want to delete this file?')) {
      return;
    }

    try {
      await api.delete(API_ENDPOINTS.policyRulebook.delete(fileId));
      setSuccess('File deleted successfully');
      loadUploadedFiles();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete file');
    }
  };

  const handleViewFile = async (file) => {
    try {
      const response = await api.get(API_ENDPOINTS.policyRulebook.download(file.id), {
        responseType: 'blob',
      });
      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', file.name || 'document');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      const msg = err.response?.data instanceof Blob
        ? 'Failed to download file'
        : (err.response?.data?.message || err.message || 'Failed to download file');
      setError(msg);
    }
  };

  const toggleRuleStatus = (ruleId) => {
    setRules((prev) =>
      prev.map((rule) =>
        rule.id === ruleId
          ? { ...rule, status: rule.status === 'active' ? 'inactive' : 'active' }
          : rule
      )
    );
  };

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
        Policy Rulebook
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* File Upload Section */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">Policy Documents</Typography>
              <input
                accept=".pdf,.doc,.docx"
                style={{ display: 'none' }}
                id="upload-file"
                type="file"
                onChange={handleFileSelect}
              />
              <label htmlFor="upload-file">
                <Button
                  variant="contained"
                  component="span"
                  startIcon={<UploadIcon />}
                  disabled={!projectId}
                >
                  Upload Document
                </Button>
              </label>
            </Box>

            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress />
              </Box>
            ) : (
              <List>
                {uploadedFiles.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>
                    No documents uploaded yet
                  </Typography>
                ) : (
                  uploadedFiles.map((file) => (
                    <ListItem key={file.id} divider>
                      <ListItemText
                        primary={file.name}
                        secondary={`Uploaded: ${file.uploadDate} • Size: ${file.size}`}
                      />
                      <ListItemSecondaryAction>
                        <IconButton
                          edge="end"
                          onClick={() => handleViewFile(file)}
                          sx={{ mr: 1 }}
                        >
                          <VisibilityIcon />
                        </IconButton>
                        <IconButton edge="end" onClick={() => handleDelete(file.id)} color="error">
                          <DeleteIcon />
                        </IconButton>
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))
                )}
              </List>
            )}
          </Paper>
        </Grid>

        {/* Rules Section */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <RuleIcon sx={{ mr: 1, color: 'primary.main' }} />
              <Typography variant="h6">Rule Implementation</Typography>
            </Box>

            <Box sx={{ mt: 2 }}>
              {rules.map((rule) => (
                <Card key={rule.id} sx={{ mb: 2 }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 1 }}>
                      <Box>
                        <Typography variant="subtitle1" fontWeight="bold">
                          {rule.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {rule.description}
                        </Typography>
                      </Box>
                      <Chip
                        label={rule.status}
                        color={rule.status === 'active' ? 'success' : 'default'}
                        size="small"
                        icon={rule.status === 'active' ? <CheckCircleIcon /> : <ErrorIcon />}
                      />
                    </Box>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => toggleRuleStatus(rule.id)}
                      sx={{ mt: 1 }}
                    >
                      {rule.status === 'active' ? 'Deactivate' : 'Activate'}
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* Upload Dialog */}
      <Dialog open={uploadDialogOpen} onClose={() => setUploadDialogOpen(false)}>
        <DialogTitle>Upload Policy Document</DialogTitle>
        <DialogContent>
          {selectedFile && (
            <TextField
              fullWidth
              label="File Name"
              value={selectedFile.name}
              disabled
              sx={{ mt: 1 }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUploadDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleUpload}
            variant="contained"
            disabled={uploading}
            startIcon={uploading ? <CircularProgress size={16} /> : <UploadIcon />}
          >
            {uploading ? 'Uploading...' : 'Upload'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PolicyRulebook;

